import { FC, useEffect, useState } from "react";
import {
  Button,
  color,
  InputGroup,
  InputSelect,
  PhoneInputGroup,
  NO_REGEX,
} from "@funded-here-interface/common";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import {
  CountryCode,
  countries,
  countryNameToCodeMap,
} from "@funded-here-interface/common/src/constant/country";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDateDraft } from "@funded-here-interface/common/src/Utils/formatter";
import { Industry, IndustryGroup } from "../../constants/enums";
import {
  IndustryGroupMap,
  LegalStructuresMerchantAndSPV,
} from "../../constants/dropdownValues";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import { useStyles } from "../../styles/BizProfileCss";
import {
  getIndonesiaStateOptions,
  getStateIdByName,
} from "@funded-here-interface/common/src/constant/indonesia";
import { useGetIndonesiaCitiesQuery } from "@funded-here-interface/common/src/hooks/useGetIndonesiaCities";
import { IndonesiaCity } from "@funded-here-interface/common/src/services/getIndonesiaCities";

// Custom hooks
import { useBorrowerBizDraftSync } from "./hooks/useBorrowerBizDraftSync";

interface BorrowerBizProfileProps {
  onDirtyChange?: (isDirty: boolean) => void;
  onSaveDraft?: (callback: () => void) => void;
  onResetForm?: (callback: () => void) => void;
  errors?: { [key: string]: string };
  onClearError?: (field: string) => void;
}

const BorrowerBizProfile: FC<BorrowerBizProfileProps> = ({
  onDirtyChange,
  onSaveDraft,
  onResetForm,
  errors,
  onClearError,
}) => {
  const { classes } = useStyles();
  const { userId, token } = useSelector((state: RootState) => state.auth);

  // Redux selectors
  const {
    company: comp,
    regNumber: regno,
    legalStructure: legalStructureRedux,
    legalStructureOthers: legalStructureOthersRedux,
    industryGroup: industryGroupRedux,
    industryDropdownValues: industryDropdownValuesRedux,
    industryOthers: industryOthersRedux,
    typeOfInstitute,
    countryOfDomicile: countryOfDomicileRedux,
    countryOfIncorp: countryOfIncorpRedux,
    address: addressRedux,
    postal: postalRedux,
    website: companysiteRedux,
    phoneNumber: phoneNumberRedux,
    incorporationDate: incorporationDateRedux,
  } = useSelector((state: RootState) => state.borBiz);

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Custom hooks
  const { syncBorrowerBizToRedux } = useBorrowerBizDraftSync();

  // Page and dirty state
  const [initPage, setInitPage] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [shouldMarkDirty, setShouldMarkDirty] = useState(true);
  const [initialDraftData, setInitialDraftData] = useState<any>(null);

  // State for Indonesia region selection
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);

  // Local state
  const [companyName, setCompany] = useState(comp || "");
  const [registrationNumber, setRegNumber] = useState(regno || "");
  const [legalStructure, setLegStr] = useState(legalStructureRedux || "");
  const [industryGroup, setIndustryGroup] = useState(industryGroupRedux || "");
  const [industryDropdownValues, setIndustryDropdownValues] = useState<
    Industry[]
  >(industryDropdownValuesRedux || []);
  const [industryOthers, setIndustryOthers] = useState(
    industryOthersRedux || ""
  );
  const [institutionType, setInstitute] = useState(typeOfInstitute || "");
  const [countryOfIncorporation, setCountryCountryOfIncorp] = useState(
    countryOfIncorpRedux || ""
  );
  const [countryOfDomicile, setCountryCountryOfDomicile] = useState(
    countryOfDomicileRedux || ""
  );
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState(addressRedux || "");
  const [postal, setPostalCode] = useState(postalRedux || "");
  const [companyWebsite, setcompanysite] = useState(companysiteRedux || "");
  const [phoneNumber, setPhone] = useState(phoneNumberRedux || "");
  const [countryCode, setCountryCode] = useState<CountryCode | undefined>();
  const [incorporationDate, setIncorporationDate] = useState(
    incorporationDateRedux || ""
  );

  // countryOfDomicile — borrower business domicile
  const isIndonesia = countryOfDomicile === "Indonesia";

  // Indonesia cities query with caching - only fetches when stateId changes
  const { data: citiesData, isLoading: isCitiesLoading } =
    useGetIndonesiaCitiesQuery({
      stateId: selectedStateId,
      token,
    });

  // Derive city options from cached query data
  const cityOptions = citiesData?.map((c: IndonesiaCity) => c.value) || [];

  /**
   * Generic input change handler with dirty tracking and error clearing
   */
  const handleInputChange = (
    setter: Function,
    value: any,
    errorField?: string
  ) => {
    // Prevent future dates for date fields
    if (errorField === "incorporationDate" && value && new Date(value) > new Date()) {
      toast.error("Date of Incorporation cannot be in the future");
      return;
    }
    setter(value);
    if (shouldMarkDirty) {
      setIsDirty(true);
      onDirtyChange?.(true);
    }
    if (errorField) {
      onClearError?.(errorField);
    }
  };

  /**
   * Handle legal structure change
   */
  const onLegalStructureChange = (value: string) => {
    handleInputChange(setLegStr, value, "legalStructure");
  };

  /**
   * Handle industry group change
   */
  const onIndustryGroupChange = (value: IndustryGroup) => {
    setIndustryDropdownValues([]);
    setInstitute("");
    const industry = IndustryGroupMap[value] || [];
    handleInputChange(setIndustryGroup, value, "industryGroup");
    handleInputChange(setIndustryDropdownValues, [...industry]);
  };

  /**
   * Handle industry change
   */
  const onIndustryChange = (value: Industry) => {
    handleInputChange(setInstitute, value, "institutionType");
  };

  /**
   * Handle country of domicile change (also updates phone country code)
   */
  const handleCountryOfDomicileChange = (value: string) => {
    handleInputChange(setCountryCountryOfDomicile, value, "countryOfDomicile");
    setRegion("");
    setCity("");
    setSelectedStateId(null);
    const countryCode = countryNameToCodeMap.get(value);
    setCountryCode(countryCode ? (countryCode as CountryCode) : undefined);
  };

  /**
   * Handle region change - sets stateId which triggers cached city fetch
   */
  const handleRegionChange = (value: string) => {
    handleInputChange(setRegion, value, "region");
    setCity("");
    const stateId = getStateIdByName(value);
    setSelectedStateId(stateId || null);
  };

  /**
   * Handle city change
   */
  const handleCityChange = (value: string) => {
    handleInputChange(setCity, value, "city");
  };

  /**
   * Load draft data from backend and sync to Redux
   */
  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          setShouldMarkDirty(false);
          const params = { userId: parseInt(userId), token };
          const data = await getOrgDraftMutation.mutateAsync(params);

          if (data) {
            const dateToChange = formatDateDraft(data?.companyStartDate);

            setInitialDraftData(data);

            // Set local states
            setCompany(data.companyName || "");
            setRegNumber(data.companyRegistrationNo || "");
            setLegStr(data.legalStructure || "");
            setCountryCountryOfIncorp(data.countryOfIncorporation || "");
            setCountryCountryOfDomicile(data.countryOfDomicile || "");
            setRegion(data.region || "");
            setCity(data.city || "");
            setAddress(data.legalEntityAddress || "");
            setPostalCode(data.postalCode?.toString() || "");
            setcompanysite(data.website || "");
            setPhone(data.phoneNumber || "");
            setIncorporationDate(dateToChange || "");
            setIndustryGroup(data.industryGroup || "");
            setInstitute(data.industry || "");
            setIndustryOthers(data.industryOthers || "");

            // If Indonesia and region is set, set stateId to trigger cached city fetch
            if (data.countryOfDomicile === "Indonesia" && data.region) {
              const stateId = getStateIdByName(data.region);
              setSelectedStateId(stateId || null);
            }

            // Set industry dropdown values
            if (data.industryGroup) {
              const industry =
                IndustryGroupMap[data.industryGroup as IndustryGroup] || [];
              setIndustryDropdownValues([...industry]);
            }

            // Set country code
            setCountryCode(
              (countryNameToCodeMap.get(
                data.countryOfDomicile
              ) as CountryCode) || undefined
            );

            // Sync to Redux
            syncBorrowerBizToRedux({
              company: data.companyName || "",
              regNumber: data.companyRegistrationNo || "",
              legalStructure: data.legalStructure || "",
              industryGroup: data.industryGroup || "",
              industryDropdownValues:
                IndustryGroupMap[data.industryGroup as IndustryGroup] || [],
              industryOthers: data.industryOthers || "",
              typeOfInstitute: data.industry || "",
              countryOfIncorp: data.countryOfIncorporation || "",
              countryOfDomicile: data.countryOfDomicile || "",
              region: data.region || "",
              city: data.city || "",
              address: data.legalEntityAddress || "",
              postal: data.postalCode?.toString() || "",
              website: data.website || "",
              phoneNumber: data.phoneNumber || "",
              incorporationDate: dateToChange || "",
            });

            setIsDirty(false);
            onDirtyChange?.(false);
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
          setShouldMarkDirty(true);
        }
      }
    };

    initializeDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initPage, userId, token]);

  /**
   * Save draft to backend
   */
  const saveDraft = (showToast: boolean = true) => {
    const formattedPhoneNumber = phoneNumber.startsWith("+")
      ? phoneNumber
      : `+${phoneNumber}`;

    // Sync to Redux
    syncBorrowerBizToRedux({
      company: companyName,
      regNumber: registrationNumber,
      legalStructure,
      industryGroup,
      industryDropdownValues,
      industryOthers,
      typeOfInstitute: institutionType,
      countryOfIncorp: countryOfIncorporation,
      countryOfDomicile,
      region,
      city,
      address,
      postal,
      website: companyWebsite,
      phoneNumber: formattedPhoneNumber,
      incorporationDate,
    });

    // If country is not Indonesia, explicitly clear region and city
    const clearFields: ("region" | "city")[] = !isIndonesia
      ? ["region", "city"]
      : [];

    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        companyName,
        companyRegistrationNo: registrationNumber,
        legalStructure,
        industryGroup,
        industry: institutionType,
        countryOfIncorporation,
        countryOfDomicile,
        region: region || null,
        city: city || null,
        legalEntityAddress: address,
        postalCode: postal ? postal : undefined,
        website: companyWebsite,
        companyStartDate: incorporationDate,
        phoneNumber: formattedPhoneNumber,
        clearFields,
      },
      {
        onSuccess: () => {
          setIsDirty(false);
          onDirtyChange?.(false);
          setInitialDraftData({
            companyName,
            companyRegistrationNo: registrationNumber,
            legalStructure,
            industryGroup,
            industry: institutionType,
            industryOthers,
            countryOfIncorporation,
            countryOfDomicile,
            region: region || undefined,
            city: city || undefined,
            legalEntityAddress: address,
            postalCode: postal || undefined,
            website: companyWebsite,
            phoneNumber: formattedPhoneNumber,
            companyStartDate: incorporationDate,
          });
          if (showToast) {
            toast.success("Draft Saved", { autoClose: 2000 });
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  /**
   * Reset form to initial draft data
   */
  const resetForm = () => {
    if (initialDraftData) {
      // Set local states
      setCompany(initialDraftData.companyName || "");
      setRegNumber(initialDraftData.companyRegistrationNo || "");
      setLegStr(initialDraftData.legalStructure || "");
      setIndustryGroup(initialDraftData.industryGroup || "");
      setIndustryDropdownValues(
        IndustryGroupMap[initialDraftData.industryGroup as IndustryGroup] || []
      );
      setInstitute(initialDraftData.industry || "");
      setIndustryOthers(initialDraftData.industryOthers || "");
      setCountryCountryOfIncorp(initialDraftData.countryOfIncorporation || "");
      setCountryCountryOfDomicile(initialDraftData.countryOfDomicile || "");
      setAddress(initialDraftData.legalEntityAddress || "");
      setPostalCode(initialDraftData.postalCode?.toString() || "");
      setcompanysite(initialDraftData.website || "");
      setPhone(initialDraftData.phoneNumber || "");
      setIncorporationDate(initialDraftData.incorporationDate || "");
      setRegion(initialDraftData.region || "");
      setCity(initialDraftData.city || "");
      setSelectedStateId(null);
      setCountryCode(
        (countryNameToCodeMap.get(
          initialDraftData.countryOfDomicile
        ) as CountryCode) || undefined
      );

      // Sync to Redux
      syncBorrowerBizToRedux({
        company: initialDraftData.companyName || "",
        regNumber: initialDraftData.companyRegistrationNo || "",
        legalStructure: initialDraftData.legalStructure || "",
        industryGroup: initialDraftData.industryGroup || "",
        industryDropdownValues:
          IndustryGroupMap[initialDraftData.industryGroup as IndustryGroup] ||
          [],
        industryOthers: initialDraftData.industryOthers || "",
        typeOfInstitute: initialDraftData.industry || "",
        countryOfIncorp: initialDraftData.countryOfIncorporation || "",
        countryOfDomicile: initialDraftData.countryOfDomicile || "",
        region: initialDraftData.region || "",
        city: initialDraftData.city || "",
        address: initialDraftData.legalEntityAddress || "",
        postal: initialDraftData.postalCode?.toString() || "",
        website: initialDraftData.website || "",
        phoneNumber: initialDraftData.phoneNumber || "",
        incorporationDate: initialDraftData.incorporationDate || "",
      });

      setIsDirty(false);
      onDirtyChange?.(false);
    }
  };

  /**
   * Register save draft and reset form callbacks
   */
  useEffect(() => {
    if (onSaveDraft) {
      onSaveDraft(saveDraft);
    }
    if (onResetForm) {
      onResetForm(resetForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveDraft, onResetForm]);

  return (
    <div style={{ marginTop: "20px" }} className={classes.stepContent}>
      <InputGroup
        isErrored={errors?.companyName}
        errorMessage={!!errors?.companyName}
        onChange={(value: string) =>
          handleInputChange(setCompany, value, "companyName")
        }
        labelText="Company Name"
        id="companyName"
        value={companyName}
      />

      <InputGroup
        isErrored={errors?.registrationNumber}
        errorMessage={!!errors?.registrationNumber}
        onChange={(value: string) =>
          handleInputChange(setRegNumber, value, "registrationNumber")
        }
        labelText="Registration Number"
        id="registrationNumber"
        value={registrationNumber}
      />

      <InputGroup
        isErrored={errors?.incorporationDate}
        errorMessage={!!errors?.incorporationDate}
        onChange={(value: any) =>
          handleInputChange(setIncorporationDate, value, "incorporationDate")
        }
        labelText="Date of Incorporation"
        value={incorporationDate}
        id="incorporationDate"
        type="date"
        max={new Date().toISOString().split("T")[0]}
      />

      <InputSelect
        isErrored={errors?.legalStructure}
        errorMessage={!!errors?.legalStructure}
        onChange={onLegalStructureChange}
        labelText="Legal Structure"
        id="legalStructure"
        value={legalStructure}
        data={LegalStructuresMerchantAndSPV}
      />

      <InputSelect
        isErrored={errors?.industryGroup}
        errorMessage={!!errors?.industryGroup}
        onChange={onIndustryGroupChange}
        labelText="Industry Group"
        value={industryGroup}
        id="industryGroup"
        data={Object.values(IndustryGroup).sort((a, b) => a.localeCompare(b))}
      />

      <InputSelect
        isErrored={errors?.institutionType}
        errorMessage={!!errors?.institutionType}
        onChange={onIndustryChange}
        labelText="Industry"
        value={institutionType}
        id="institutionType"
        data={[...industryDropdownValues].sort((a, b) => a.localeCompare(b))}
      />

      <InputSelect
        isErrored={errors?.countryOfIncorporation}
        errorMessage={!!errors?.countryOfIncorporation}
        onChange={(value: string) =>
          handleInputChange(
            setCountryCountryOfIncorp,
            value,
            "countryOfIncorporation"
          )
        }
        labelText="Country of Incorporation"
        id="countryOfIncorporation"
        value={countryOfIncorporation}
        data={countries}
      />

      <InputSelect
        isErrored={errors?.countryOfDomicile}
        errorMessage={!!errors?.countryOfDomicile}
        onChange={handleCountryOfDomicileChange}
        labelText="Country of Domicile"
        id="countryOfDomicile"
        value={countryOfDomicile}
        data={countries}
      />

      {isIndonesia && (
        <>
          <InputSelect
            isErrored={errors?.region}
            errorMessage={!!errors?.region}
            onChange={handleRegionChange}
            labelText="Region"
            id="region"
            value={region}
            data={getIndonesiaStateOptions()}
          />
          <InputSelect
            isErrored={errors?.city}
            errorMessage={!!errors?.city}
            onChange={handleCityChange}
            labelText={isCitiesLoading ? "City (Loading...)" : "City"}
            id="city"
            value={city}
            data={cityOptions}
            disabled={!region || isCitiesLoading}
          />
        </>
      )}

      <InputGroup
        isErrored={errors?.address}
        errorMessage={!!errors?.address}
        onChange={(value: string) =>
          handleInputChange(setAddress, value, "address")
        }
        labelText="Address"
        id="address"
        value={address}
      />

      <InputGroup
        isErrored={errors?.postalcode}
        errorMessage={!!errors?.postalcode}
        onChange={(value: string) =>
          handleInputChange(
            setPostalCode,
            value.replace(NO_REGEX, ""),
            "postalcode"
          )
        }
        labelText="Postal Code"
        id="postalcode"
        value={postal}
      />

      <InputGroup
        isErrored={errors?.companyWebsite}
        errorMessage={!!errors?.companyWebsite}
        onChange={(value: string) =>
          handleInputChange(setcompanysite, value, "companyWebsite")
        }
        labelText="Company Website / Online Marketplace Link"
        id="companyWebsite"
        value={companyWebsite}
      />

      <div style={{ marginBottom: "20px" }}>
        <PhoneInputGroup
          isErrored={errors?.phoneNumber}
          value={
            phoneNumber && !phoneNumber.startsWith("+")
              ? `+${phoneNumber}`
              : phoneNumber
          }
          country={countryCode}
          onChange={(value: string) =>
            handleInputChange(setPhone, value || "", "phoneNumber")
          }
        />
      </div>

      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft(true)}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
        />
      </div>
    </div>
  );
};

export default BorrowerBizProfile;
